import { useState } from 'react';
import api from '../api/axios';

const C = { green: '#3cb74e', black: '#111111', white: '#ffffff', red: '#ef4444', amber: '#f59e0b', gray: '#64748b', border: '#e2e8f0', lightBg: '#f8fdf8' };
const statusColors = {
  valid:   { bg: '#dcfce7', text: '#166534' },
  invalid: { bg: '#fee2e2', text: '#991b1b' },
  risky:   { bg: '#fef3c7', text: '#92400e' },
  unknown: { bg: '#f1f5f9', text: '#475569' },
};
const statusIcons = { valid: '✅', invalid: '❌', risky: '⚠️', unknown: '❓' };

// jobId is optional — passed from BulkVerifier to scope exports to that job
export default function ResultsTable({ results = [], jobId = null }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(1);
  const perPage = 20;

  const counts = results.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const filtered = results.filter(r => {
    const matchStatus = filter === 'all' || r.status === filter;
    const matchSearch = !search || r.email.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const paginated  = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  // Export scoped to jobId + active filter
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (jobId) params.set('job_id', jobId);
      if (filter !== 'all') params.set('status', filter);
      const qs  = params.toString();
      const res = await api.get(`/export/csv${qs ? '?' + qs : ''}`, { responseType: 'blob' });
      const label = filter === 'all' ? 'all' : filter;
      const name  = jobId ? `job${jobId}_${label}.csv` : `history_${label}.csv`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = name;
      a.click();
    } catch { alert('Export failed'); }
  };

  return (
    <div style={s.container}>
      {/* Filter + Search + Export bar */}
      <div style={s.filterBar}>
        <div style={s.filterLeft}>
          {['all', 'valid', 'invalid', 'risky', 'unknown'].map(f => {
            const isActive = filter === f;
            const sc = statusColors[f];
            return (
              <button key={f}
                style={{
                  ...s.filterBtn,
                  ...(isActive ? {
                    background: f === 'all' ? C.green : sc.bg,
                    color:      f === 'all' ? C.white : sc.text,
                    border:     `1.5px solid ${f === 'all' ? C.green : sc.text}`,
                    fontWeight: 700,
                  } : {}),
                }}
                onClick={() => { setFilter(f); setPage(1); }}>
                {f === 'all'
                  ? `All (${results.length})`
                  : `${statusIcons[f]} ${f.charAt(0).toUpperCase() + f.slice(1)} (${counts[f] || 0})`}
              </button>
            );
          })}
        </div>
        <div style={s.filterRight}>
          <input
            style={s.search}
            placeholder="🔍 Search email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {/* Export only what is currently filtered */}
          <button style={s.exportBtn} onClick={handleExport}>
            ⬇️ Export {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)} ({filtered.length})
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr style={s.theadRow}>
              {['#', 'Email', 'Format', 'MX Record', 'SMTP', 'Catch-all', 'Disposable', 'Status'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((r, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.lightBg }}>
                <td style={s.tdNum}>{(page - 1) * perPage + i + 1}</td>
                <td style={s.tdEmail}>{r.email}</td>
                <td style={s.tdCenter}>{r.format_valid  ? '✅' : '❌'}</td>
                <td style={s.tdCenter}>{r.mx_valid      ? '✅' : '❌'}</td>
                <td style={s.tdCenter}>{r.smtp_valid    ? '✅' : '❌'}</td>
                <td style={s.tdCenter}>{r.is_catchall   ? <span style={s.warnBadge('#fef3c7','#92400e')}>⚠️ Yes</span> : '—'}</td>
                <td style={s.tdCenter}>{r.is_disposable ? <span style={s.warnBadge('#fee2e2','#991b1b')}>⚠️ Yes</span> : '—'}</td>
                <td style={s.tdCenter}>
                  <span style={{ ...s.statusBadge, background: statusColors[r.status]?.bg, color: statusColors[r.status]?.text }}>
                    {statusIcons[r.status]} {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {paginated.length === 0 && <p style={s.empty}>No results found</p>}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={s.pagination}>
          <span style={s.pageInfo}>
            Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
          </span>
          <div style={s.pageButtons}>
            <button style={s.pageBtn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button key={p}
                  style={{ ...s.pageBtn, ...(p === page ? s.pageBtnActive : {}) }}
                  onClick={() => setPage(p)}>{p}</button>
              );
            })}
            <button style={s.pageBtn} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  container:    { background: C.white, borderRadius: 14, border: `1.5px solid ${C.border}`, overflow: 'hidden', marginTop: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  filterBar:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: C.lightBg, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 10 },
  filterLeft:   { display: 'flex', gap: 6, flexWrap: 'wrap' },
  filterBtn:    { padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${C.border}`, background: C.white, color: C.gray, cursor: 'pointer', fontSize: 12, fontWeight: 500, transition: 'all .15s' },
  filterRight:  { display: 'flex', gap: 8, alignItems: 'center' },
  search:       { padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, outline: 'none', width: 200, color: C.black, background: C.white },
  exportBtn:    { padding: '8px 16px', background: C.green, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' },
  tableWrap:    { overflowX: 'auto' },
  table:        { width: '100%', borderCollapse: 'collapse' },
  theadRow:     { background: C.black },
  th:           { padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: C.white, whiteSpace: 'nowrap' },
  tdNum:        { padding: '10px 14px', color: C.gray, fontSize: 12, minWidth: 40 },
  tdEmail:      { padding: '10px 14px', color: C.black, fontSize: 13, fontWeight: 500 },
  tdCenter:     { padding: '10px 14px', textAlign: 'center', fontSize: 13 },
  statusBadge:  { padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, textTransform: 'capitalize', display: 'inline-block' },
  warnBadge:    (bg, color) => ({ background: bg, color, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, display: 'inline-block' }),
  empty:        { textAlign: 'center', padding: 40, color: C.gray },
  pagination:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: `1px solid ${C.border}` },
  pageInfo:     { color: C.gray, fontSize: 13 },
  pageButtons:  { display: 'flex', gap: 4 },
  pageBtn:      { padding: '6px 12px', borderRadius: 6, border: `1.5px solid ${C.border}`, background: C.white, color: C.black, cursor: 'pointer', fontSize: 13 },
  pageBtnActive:{ background: C.green, color: C.white, border: `1.5px solid ${C.green}` },
};
